import { type ReactNode, createContext, useContext } from "react";

import type { Citation } from "@/components/chat/CitationCard";

/** Lets an inline `[doc-N]` / `[self-N]` chip open the source detail sheet on
 * click — the same source detail the `SourcesBlock` titles open.
 * `AssistantTurn` (and the research full-report section) provide the
 * `onSourceClick` callback `ChatInner` owns (`openSourceDetail`). With a null
 * callback (outside a turn) the chip still renders but the click is inert. */
export const CitationDetailContext = createContext<{
  onSourceClick: ((sourceId: string) => void) | null;
}>({ onSourceClick: null });

// Match `[doc-N]` and `[self-N]` tokens. The two prefixes carry
// independent counters on the backend (see `src/chat/sse.py`): `doc-N`
// indexes the customer's citations and `self-N` indexes the curated
// self-knowledge corpus. The capturing groups give us (prefix, n).
const CITATION_TOKEN_RE = /\[(doc|self)-(\d+)\]/g;

interface Resolved {
  prefix: "doc" | "self";
  n: number;
  citation: Citation;
}

// Stub used when a token is encountered before its matching `citation` SSE
// event has landed. Renders the chip with full styling so the operator sees
// the visual anchor immediately; the empty `id` keeps the click inert until
// the real citation streams in and the markdown re-renders.
const PENDING_CITATION: Citation = {
  id: "",
  title: "Cargando fuente…",
  preview: "",
};

/** Resolve a `[doc-N]` / `[self-N]` token against the citation list of the
 * assistant turn it belongs to. Self and doc tokens are keyed by their
 * 1-based ordinal within their own subset, so `[doc-1]` always points at
 * the first non-self citation and `[self-1]` at the first self citation
 * — regardless of the order the events arrived in the SSE stream. */
function resolve(prefix: "doc" | "self", n: number, citations: Citation[]): Resolved | null {
  const subset =
    prefix === "self"
      ? citations.filter((c) => c.source === "self")
      : citations.filter((c) => c.source !== "self");
  const citation = subset[n - 1];
  if (!citation) return null;
  return { prefix, n, citation };
}

// Anchored variant of `CITATION_TOKEN_RE` for parsing a whole label string
// (e.g. `ResponseAssetProjection.citation_label`, which the backend emits as
// the full bracketed token — `"[doc-3]"` — via
// `TurnCitationRegistry._next_label`) rather than scanning inside markdown.
const CITATION_LABEL_RE = /^\[(doc|self)-(\d+)\]$/;

/** Parse a standalone `"[doc-N]"` / `"[self-N]"` citation label into its
 * prefix + 1-based ordinal, or `null` if it doesn't match that shape. */
export function parseCitationLabel(label: string): { prefix: "doc" | "self"; n: number } | null {
  const match = CITATION_LABEL_RE.exec(label);
  if (!match) return null;
  const prefix = match[1];
  const n = match[2];
  if ((prefix !== "doc" && prefix !== "self") || n === undefined) return null;
  return { prefix, n: Number(n) };
}

/** Resolve a `ResponseAssetProjection.citation_label` (e.g. `"[doc-3]"`)
 * against a turn's citation list, using the same ordinal-within-subset
 * lookup the inline `[doc-N]` markdown chips use — so a knowledge-asset
 * card's "go to source" affordance opens the exact same source detail sheet
 * as clicking the matching inline chip. Returns `null` when the label
 * doesn't parse or has no matching citation (e.g. it streamed in before its
 * `citation` SSE event landed). */
export function resolveCitationLabel(label: string, citations: Citation[]): Citation | null {
  const parsed = parseCitationLabel(label);
  if (!parsed) return null;
  return resolve(parsed.prefix, parsed.n, citations)?.citation ?? null;
}

interface ChipProps {
  prefix: "doc" | "self";
  n: number;
  citation: Citation;
}

function CitationChip({ prefix, n, citation }: ChipProps): ReactNode {
  const { onSourceClick } = useContext(CitationDetailContext);
  // Clicking the chip opens the same `SourceDetailSheet` as clicking the
  // source's title in the `SourcesBlock`. Inert when the citation hasn't
  // resolved yet (empty `id` while it streams in) or no callback is in scope.
  const canOpen = onSourceClick !== null && citation.id !== "";
  // Match the footer metadata pills in `routes/chat.tsx` (`vec N`,
  // `bm25 N`, `fused N`, etc.) so citations feel like part of the
  // same chrome rather than a separate accent. Same `border-border`
  // outline, `rounded-sm`, `py-px` — only the text dims to
  // `muted-foreground` at rest and climbs to `foreground` on hover
  // so the chip stays out of the way while reading.
  const chipClasses =
    "mx-0.5 inline-flex items-center rounded-sm border border-border px-1.5 py-px text-label-2 leading-none align-baseline cursor-pointer transition-colors text-muted-foreground hover:text-foreground hover:border-foreground/40";
  return (
    <button
      type="button"
      data-citation-prefix={prefix}
      data-citation-n={n}
      aria-label={`${prefix}-${n}: ${citation.title}`}
      className={chipClasses}
      onClick={() => {
        if (canOpen) onSourceClick(citation.id);
      }}
    >
      {prefix}-{n}
    </button>
  );
}

/** Walk `text` left-to-right, replacing every `[doc-N]` / `[self-N]`
 * occurrence with a `<CitationChip />`. Tokens that don't resolve against
 * the citation list (eg. `[doc-99]` with only 3 doc citations) fall back
 * to literal text — the reader still sees the original markdown rather
 * than a dead chip. Returns an array of `ReactNode`s suitable for
 * rendering inline inside `<p>`, `<li>`, `<td>`, etc. */
export function renderCitationTokens(text: string, citations: Citation[]): ReactNode[] {
  const out: ReactNode[] = [];
  let cursor = 0;
  // Each regex.exec call advances `lastIndex` because of the /g flag.
  // Reset before iterating so the helper is idempotent across renders.
  CITATION_TOKEN_RE.lastIndex = 0;
  let match = CITATION_TOKEN_RE.exec(text);
  let keyCounter = 0;
  while (match !== null) {
    const [token, prefixRaw, nRaw] = match;
    const prefix = prefixRaw as "doc" | "self";
    const n = Number.parseInt(nRaw ?? "", 10);
    const start = match.index;
    if (start > cursor) {
      out.push(text.slice(cursor, start));
    }
    if (!Number.isFinite(n)) {
      // Malformed N — keep the original substring.
      out.push(token);
    } else {
      // Render the chip eagerly even before the matching citation event has
      // arrived from the SSE stream: the operator gets the styled anchor as
      // soon as the token streams in, and the click wires up once the
      // citation lands (markdownComponents re-memoises on citations).
      const resolved = resolve(prefix, n, citations);
      keyCounter += 1;
      out.push(
        <CitationChip
          key={`${prefix}-${n}-${keyCounter}`}
          prefix={prefix}
          n={n}
          citation={resolved?.citation ?? PENDING_CITATION}
        />,
      );
    }
    cursor = start + token.length;
    match = CITATION_TOKEN_RE.exec(text);
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
